import { Button, Card, CardContent, Grid, Tooltip, Typography } from '@mui/material';

import { FormInput } from '../../../components/hook-form/form-input.component';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { BuildingFloorUnitForm } from './building-floor-unit.form';
import { CreateBuildingFloorUnitButton } from './create-building-floor-unit.button';
import { Building } from '../../../models/building';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { DeleteButton } from '../../../components/delete-button.component';
import { Icons } from '../../../assets/icons';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { useState } from 'react';
import { useSaveData } from '../../../hooks/useSaveData';
import { GenericFileUploadDialog } from '../../../components/file-upload-dialog.component';
interface BuildingFloorFormProps {
  name: string;
  index: number;
  building: Building;
  onRemove: (index: number) => void;
  page?: string;
  syncWithDux: any;
}

export interface SingleOrMultiLot {
  unitID: number;
  buildingID: number | null;
  unitName: string;
  unitNumber: string | null;
  floorID: number | null;
  isCommonArea: boolean | null;
  commonAreaID: number | null;
  address: string;
  city: string | null;
  stateID: number | null;
  zipcode: string | null;
  countyID: number | null;
  municipalityID: number | null;
  jobNumber: string;
  jobsite: string | null;
  proctorResult: string | null;
  disableDelete: boolean | null;
  isUnitCreationCompleted: boolean | null;
  squareFeet: number;
}

export interface LotData {
  projectID: number | null;
  projectBuildings: any; // Replace `any` with the appropriate type if known
  singleOrMultiLots: SingleOrMultiLot[];
}

export const BuildingFloorForm = ({ name, index, building, onRemove, page, syncWithDux }: BuildingFloorFormProps) => {
  const { control, getValues, setValue } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `${name}.buildingLots`,
    keyName: 'uID',
  });
  const [isBulkLotDialogOpen, setIsBulkLotDialogOpen] = useState<boolean>(false);
  const [isBulkAddressDialogOpen, setIsBulkAddressDialogOpen] = useState<boolean>(false);
  const { isLoading: isLoadingFile, mutateAsync: UploadFileForBulkLotOrAddressCreation } = useSaveData<LotData>(
    projectsApiKeyStore.uploadFileForBulkLotOrAddressCreation()
  );
  const { isLoading: isLoadingFileForAddressUpload, mutateAsync: UploadFileForBulkAddressCreation } =
    useSaveData<LotData>(projectsApiKeyStore.uploadFileForBulkAddressCreation());

  const handleNewUnitAdded = (unit: any) => {
    append({ ...unit, isUnitCreationCompleted: true });
    setValue(`${name}.unitsCount`, fields?.length + 1 || 0);
  };
  const handleRemove = (index: number) => {
    remove(index);
    setValue(`${name}.unitsCount`, fields?.length !== 0 ? fields?.length - 1 : fields?.length || 0);
  };

  const handleSubmitBulkLotCreationFileSave = async (file: File | null) => {
    if (!file || !getValues('projectID')) return;

    try {
      const formData = new FormData();
      formData.append('ProjectID', getValues('projectID').toString());
      formData.append('BuildingID', getValues(`${name}.buildingID`).toString());
      formData.append('FloorID', getValues(`${name}.floorID`).toString());

      formData.append('DataFile', file);

      const response = await UploadFileForBulkLotOrAddressCreation({ data: formData });

      const lots = response?.data?.singleOrMultiLots;
      if (Array.isArray(lots)) {
        lots.forEach((lot) => append(lot));
      }
    } catch (error) {
      console.error('Bulk lot upload failed:', error);
    } finally {
      setIsBulkLotDialogOpen(false);
    }
  };

  const handleSubmitBulkAddressCreationFileSave = async (file: File | null) => {
    if (!file || !getValues('projectID')) return;

    try {
      const formData = new FormData();
      formData.append('ProjectID', getValues('projectID').toString());
      formData.append('BuildingID', getValues(`${name}.buildingID`).toString());
      formData.append('FloorID', getValues(`${name}.floorID`).toString());

      formData.append('DataFile', file);
      // const fileExtension = file?.name?.split('.').pop();
      // if (fileExtension) {
      //   formData.append('FileExtension', fileExtension); // or whatever your backend expects
      // }

      const response = await UploadFileForBulkAddressCreation({ data: formData });

      const lots = response?.data?.singleOrMultiLots;
      setValue(`${name}.buildingLots`, lots);
    } catch (error) {
      console.error('Bulk Address upload failed:', error);
    } finally {
      setIsBulkAddressDialogOpen(false);
    }
  };


  return (
    <Card elevation={0} className="floorUnitBlockWrapper">
      <CardContent className="  ">
        <Grid container spacing={0}>
          <Grid
            item
            lg={12}
            sm={12}
            xl={12}
            xs={12}
            className="d-flex floorUnitBlockContainer"
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {/* <div style={{ padding: '10px' }}>{index + 1}</div> */}
              <div style={{ paddingLeft: '5px', paddingRight: '5px' }}>
                <FormInput disabled={true} name={`${name}.floorNo`} label="Floor No." />{' '}
              </div>
              <div>
                <FormInput disabled={true} name={`${name}.unitsCount`} label="Unit" />{' '}
              </div>
              <div style={{ marginLeft: 'auto' }}>
                <DeleteButton
                  data={undefined}
                  deleteMutationConfig={() => projectsApiKeyStore.deleteFloor(getValues(`${name}.floorID`))}
                  onDeleted={() => onRemove(index)}
                >
                  <>
                    <Tooltip title={<Typography variant="body1">DELETE</Typography>}>
                      <img src={Icons.Delete} alt="DELETE" />
                    </Tooltip>
                  </>
                </DeleteButton>
              </div>
            </div>
            <div style={{ paddingLeft: '20px' }}>
              {fields.map((field, index) => (
                <BuildingFloorUnitForm
                  key={field.uID}
                  name={`${name}.buildingLots.${index}`}
                  index={index}
                  onRemove={handleRemove}
                  page={page}
                  syncWithDux={syncWithDux}
                />
              ))}
            </div>
            <CreateBuildingFloorUnitButton
              projectID={getValues('projectID')}
              buildingID={building.buildingID}
              floor={getValues(name)}
              onUnitCreated={handleNewUnitAdded}
              syncWithDux={syncWithDux}
            />
          </Grid>
          {/* --------------------------------------- */}

          <Grid
            item
            lg={12}
            sm={12}
            xl={12}
            xs={12}
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'flex-end', // Aligns to right
              gap: 1, // adds spacing between buttons
            }}
          >
            <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
              <Button
                type="button"
                onClick={() => setIsBulkAddressDialogOpen(true)}
                className="btn btn-primary btnIcon"
                variant="text"
                sx={{ p: 0, m: 0, mr: 1 }}
              >
                Bulk Address Upload
              </Button>
            </GuardedComponent>
            <GuardedComponent requiredPermissions={[UserPermissionName.ADD_UNIT]}>
              <Button
                type="button"
                onClick={() => setIsBulkLotDialogOpen(true)}
                className="btn btn-primary btnIcon"
                variant="text"
                sx={{ p: 0, m: 0, mr: 1 }}
              >
                Bulk Lot Creation
              </Button>
            </GuardedComponent>
          </Grid>

        </Grid>
      </CardContent>
      {isBulkLotDialogOpen && (
        <GenericFileUploadDialog
          title="Upload File For Bulk Lot Creation"
          defaultData={{}}
          fileRequestArgs={{}}
          isLoading={isLoadingFile}
          onSubmit={(formData) => handleSubmitBulkLotCreationFileSave(formData)}
          onClose={() => setIsBulkLotDialogOpen(false)}
          acceptedFileTypes={{
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          }}
        />
      )}
      {isBulkAddressDialogOpen && (
        <GenericFileUploadDialog
          title="Upload File For Bulk Address Creation"
          defaultData={{}}
          fileRequestArgs={{}}
          isLoading={isLoadingFileForAddressUpload}
          onSubmit={(formData) => handleSubmitBulkAddressCreationFileSave(formData)}
          onClose={() => setIsBulkAddressDialogOpen(false)}
          acceptedFileTypes={{
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          }}
        />
      )}
    </Card>
  );
};
